import * as React from "react";
import {
  AppBarColor,
  HeaderPosition,
  useLocationConfiguration,
} from "./LocationConfiguration";
import { useLanguage } from "./Language";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  Tabs,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AccountCircle } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import { MainMenu } from "./MainMenu";
import { LanguageSelector } from "./LanguageSelector";
import { AccountMenu, MenuLink } from "./AccountMenu";
import { useAccount } from "./Account";
import { UserAvatar } from "./UserAvatar";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useGlobalConfig } from "./GlobalConfig";
import { Link, useLocation } from "react-router-dom";
import { AppBarThemeColor } from "./pages/config/BrandingPage";
import LogoutIcon from "@mui/icons-material/Logout";
import { AdditionalLink } from "../dto/AdditionalLink";

interface TopNavProps {
  // color: "primary" | "secondary" | "default";
  // TODO: drop this and add the reference from locationconfiguration
  showSearchForm?: boolean;
}

export const TopNavContext = React.createContext(false);

export const TopNav: React.FC<TopNavProps> = ({
  //color,
  showSearchForm = true,
}) => {
  const theme = useTheme();
  const fullSize = !useMediaQuery(theme.breakpoints.down("md"));
  const { translate } = useLanguage();
  const { isAuthenticated, user, patron, accountSummary } = useAccount();
  const { obrEnabled } = useGlobalConfig();
  const { locationConfiguration } = useLocationConfiguration();
  const location = useLocation();
  const currentPath = location.pathname;
  const currentTab =
    ["/login", "/create-account"].find((path) => path === currentPath) || false;
  const hideMyAccountToggle =
    (currentPath.startsWith("/myaccount") ||
      currentPath.startsWith("/config")) &&
    fullSize;

  const libraryName = locationConfiguration?.libraryName;

  const [mainMenuOpen, setMainMenuOpen] = React.useState<boolean>(false);

  const accountMenuRef = React.useRef(null);

  const { appBarColor, headerPosition, showHeaderOnMobile } =
    locationConfiguration.pacSettings.displaySettings;

  const [showHeaderOnBottom, setShowHeaderOnBottom] =
    React.useState<HeaderPosition>(headerPosition);

  const color = AppBarThemeColor[appBarColor];

  const handleNavClick = (event: React.MouseEvent<HTMLElement>) => {
    setMainMenuOpen(!mainMenuOpen);
  };

  const [loginPopoverAnchorEl, setLoginPopoverAnchorEl] =
    React.useState<HTMLButtonElement | null>(null);
  const [logoutPopoverAnchorEl, setLogoutPopoverAnchorEl] =
    React.useState<HTMLButtonElement | null>(null);

  const handleAccountClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isAuthenticated) {
      setLoginPopoverAnchorEl(event.currentTarget);
    } else if (hideMyAccountToggle) {
      setLogoutPopoverAnchorEl(event.currentTarget); // set popover open relative to button
    } else {
      setLoginPopoverAnchorEl(event.currentTarget);
    }
    setMainMenuOpen(false);
  };
  React.useEffect(() => {
    // when the URL location changes, close any open menus
    setMainMenuOpen(false);
    setLoginPopoverAnchorEl(null);
    setLogoutPopoverAnchorEl(null);
  }, [location]);

  const notificationCount: number =
    (accountSummary?.arrivedHolds > 0 ? 1 : 0) +
    (accountSummary?.fees > 0 ? 1 : 0) +
    (accountSummary?.itemsOverdue > 0 ? 1 : 0) +
    (accountSummary?.status && accountSummary?.status !== "Active" ? 1 : 0);

  const alternateTimeoutPageUrl =
    locationConfiguration?.pacSettings?.useAlternateTimeoutPage &&
    locationConfiguration?.pacSettings?.alternateTimeoutPageUrl;

  const getAdditionalLink = (additionalLink: AdditionalLink) => {
    return (
      <>
        {additionalLink?.additionalLinkURL && (
          <Button
            variant="text"
            color="inherit"
            href={additionalLink?.additionalLinkURL}
            rel="noopener noreferrer"
            target={
              locationConfiguration?.pacSettings?.displaySettings
                ?.openLinksInNewTab
                ? "_blank"
                : "_self"
            }
          >
            {additionalLink?.additionalLinkText}
          </Button>
        )}
      </>
    );
  };

  const createAccountEnabled =
    locationConfiguration?.pacSettings?.obrEnabled && obrEnabled;

  const hasAdditionalLinks =
    locationConfiguration?.pacSettings?.additionalLink1?.additionalLinkURL ||
    locationConfiguration?.pacSettings?.additionalLink2?.additionalLinkURL ||
    locationConfiguration?.pacSettings?.additionalLink3?.additionalLinkURL ||
    locationConfiguration?.pacSettings?.additionalLink4?.additionalLinkURL ||
    locationConfiguration?.pacSettings?.additionalLink5?.additionalLinkURL;

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ flexGrow: 1 }}>
      <TopNavContext.Provider value={true}>
        <AppBar
          elevation={0}
          color={color}
          position="relative"
          // position={fullSize ? "relative" : "fixed"}
          // sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          {fullSize && hasAdditionalLinks && (
            <Toolbar variant="dense">
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                {getAdditionalLink(
                  locationConfiguration?.pacSettings?.additionalLink1
                )}
                {getAdditionalLink(
                  locationConfiguration?.pacSettings?.additionalLink2
                )}
                {getAdditionalLink(
                  locationConfiguration?.pacSettings?.additionalLink3
                )}
                {getAdditionalLink(
                  locationConfiguration?.pacSettings?.additionalLink4
                )}
                {getAdditionalLink(
                  locationConfiguration?.pacSettings?.additionalLink5
                )}
              </Stack>
            </Toolbar>
          )}
          <Stack
            sx={{
              flexDirection: "row",
            }}
          >
            <Toolbar sx={{ gap: { xs: 1, sm: 2 } }}>
              <IconButton
                edge="start"
                onClick={handleNavClick}
                aria-controls={mainMenuOpen ? "nav-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={mainMenuOpen ? "true" : undefined}
                size="large"
                color="inherit"
                aria-label="open drawer"
              >
                <MenuIcon />
              </IconButton>
              {locationConfiguration?.pacSettings?.displaySettings
                ?.mainPageLogoImageId && (
                <Box
                  sx={{
                    flexGrow: { xs: 1, sm: 0 },
                    justifyContent: "flex-start",
                  }}
                >
                  <Button
                    component={alternateTimeoutPageUrl ? "a" : Link}
                    href={
                      alternateTimeoutPageUrl
                        ? alternateTimeoutPageUrl
                        : undefined
                    }
                    to={alternateTimeoutPageUrl ? undefined : "/"}
                  >
                    <Avatar
                      alt={
                        locationConfiguration?.pacSettings?.displaySettings
                          ?.mainPageLogoImageAltText ||
                        (locationConfiguration?.libraryName).concat(
                          translate("logo")
                        )
                      }
                      src={`/storedimage/${locationConfiguration?.pacSettings?.displaySettings?.mainPageLogoImageId}`}
                      variant="square"
                      sx={{
                        height: 48,
                        maxWidth: "100%",
                        width: "auto",
                        "& > img": {
                          maxWidth: "100%",
                          height: "100%",
                          width: "auto",
                          objectFit: "contain",
                        },
                      }}
                    />
                  </Button>
                </Box>
              )}

              {/* GOTO library name */}
              {headerPosition === HeaderPosition.Top && (
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  sx={{
                    display: { xs: "none", sm: "block" },
                    flexGrow: { xs: 0, sm: 1 },
                  }}
                >
                  {libraryName}
                </Typography>
              )}
            </Toolbar>

            {/* Force the left margin to push this item to the right  */}
            <Toolbar
              sx={{
                gap: { xs: 1, sm: 2 },
                ml: "auto",
              }}
            >
              {/* XS, scrolled past search bar, or on a page without a search bar */}
              {(!fullSize || !showSearchForm) && (
                <IconButton
                  color="inherit"
                  aria-label="search"
                  component={Link}
                  to="/search"
                >
                  <SearchIcon></SearchIcon>
                </IconButton>
              )}

              <LanguageSelector></LanguageSelector>

              {!isAuthenticated && fullSize && (
                <Stack direction="row">
                  {createAccountEnabled && (
                    <>
                      <Button
                        color="inherit"
                        startIcon={<AccountCircle />}
                        component={Link}
                        to="/create-account"
                      >
                        {translate("obrCreateAccount")}
                      </Button>
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                      />
                    </>
                  )}
                  <Button
                    component={Link}
                    to="/myaccount/summary"
                    color="inherit"
                    startIcon={!createAccountEnabled && <AccountCircle />}
                  >
                    {translate("logon")}
                  </Button>
                </Stack>
              )}
              {!isAuthenticated && !fullSize && (
                <IconButton
                  aria-label={translate("logon")}
                  onClick={handleAccountClick}
                  edge="end"
                  color="inherit"
                >
                  <AccountCircle />
                </IconButton>
              )}

              {isAuthenticated && (
                <Box sx={{ display: { xs: "flex" } }}>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={handleAccountClick}
                    aria-controls={open ? "account-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    aria-label="account of current user"
                    color="inherit"
                  >
                    {patron && (
                      <Badge badgeContent={notificationCount} color="error">
                        <UserAvatar user={user}></UserAvatar>
                      </Badge>
                    )}
                    {!patron && <AdminPanelSettingsIcon />}
                  </IconButton>
                </Box>
              )}
            </Toolbar>
          </Stack>
          {/* mobile title bar */}

          {showHeaderOnMobile && (
            <Toolbar
              variant="dense"
              sx={{ display: { xs: "flex", sm: "none" }, alignItems: "center" }}
            >
              <Typography variant="h6" noWrap component="div">
                {libraryName}
              </Typography>
            </Toolbar>
          )}

          {headerPosition === HeaderPosition.Bottom && (
            <Toolbar
              variant="dense"
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  p: 3,
                }}
              />
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  pl: 2,
                }}
              >
                {libraryName}
              </Typography>
            </Toolbar>
          )}
        </AppBar>
      </TopNavContext.Provider>
      <Popover
        open={Boolean(logoutPopoverAnchorEl)}
        anchorEl={logoutPopoverAnchorEl}
        onClose={() => setLogoutPopoverAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <MenuList>
          <MenuItem
            component={Link}
            to="/logout"
            onClick={() => setLogoutPopoverAnchorEl(null)}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate("logout")}</ListItemText>
          </MenuItem>
        </MenuList>
      </Popover>
      <Popover
        open={
          Boolean(loginPopoverAnchorEl) && !isAuthenticated && !isSmallScreen
        }
        anchorEl={loginPopoverAnchorEl}
        onClose={() => setLoginPopoverAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <MenuList>
          {createAccountEnabled && (
            <MenuItem component={Link} to="/create-account">
              {translate("obrCreateAccount")}
            </MenuItem>
          )}
          <MenuItem component={Link} to="/myaccount/summary">
            {translate("logon")}
          </MenuItem>
        </MenuList>
      </Popover>
      <Drawer
        open={mainMenuOpen}
        onClose={() => {
          setMainMenuOpen(false);
        }}
        sx={{
          width: "320px",
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: "320px", boxSizing: "border-box" },
        }}
      >
        <MainMenu onClose={() => setMainMenuOpen(false)}></MainMenu>
      </Drawer>
      {/* Render a Drawer instead of popover on small screens */}
      {isSmallScreen ? (
        <Drawer
          open={Boolean(loginPopoverAnchorEl)}
          anchor="left"
          onClose={() => {
            setLoginPopoverAnchorEl(null);
          }}
          sx={{
            width: 320,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: 320,
              boxSizing: "border-box",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            {isAuthenticated ? (
              <AccountMenu
                accountSummary={accountSummary}
                patron={patron}
                user={user}
              />
            ) : (
              <Tabs
                value={currentTab}
                orientation={"vertical"}
                textColor="secondary"
                indicatorColor="secondary"
              >
                {createAccountEnabled && (
                  <MenuLink
                    value="/create-account"
                    label={translate("obrCreateAccount")}
                  />
                )}
                <MenuLink
                  value="/myaccount/summary"
                  label={translate("logon")}
                />
              </Tabs>
            )}
          </Box>
        </Drawer>
      ) : (
        // Large screens get a popover instead
        <Popover
          open={Boolean(loginPopoverAnchorEl) && isAuthenticated}
          anchorEl={loginPopoverAnchorEl}
          onClick={(event) => {
            if (
              accountMenuRef.current &&
              !accountMenuRef.current.contains(event.target)
            ) {
              setLoginPopoverAnchorEl(null);
            }
          }}
          slotProps={{
            paper: {
              ref: accountMenuRef,
            },
          }}
          sx={{
            width: "420px",
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: "320px",
              boxSizing: "border-box",
            },
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          {isAuthenticated && (
            <AccountMenu
              accountSummary={accountSummary}
              patron={patron}
              user={user}
            ></AccountMenu>
          )}
        </Popover>
      )}
    </Box>
  );
};
